import { useTranslation } from "react-i18next";
import { StyleSheet, Text, View } from "react-native";

import type { UiTheme } from "../theme";
import { Button } from "./Button";
import { Dialog } from "./Dialog";

// THE "leave with unsaved changes?" confirm — a pure-RN centred <Dialog>
// (Button + Dialog, both already shared), NOT the platform Alert/window.confirm
// seam (ConfirmFn in ../seams.ts): it renders identically on mobile and
// web-rnw with zero host wiring, the same reason <Dialog>/<Modal> exist (see
// Modal.tsx). One home for the discard-confirm idiom so a second mid-edit
// exit (a Plan/Program editor's back — PE-3) EXTENDS this, never rolls its
// own copy: TG-1 is the first caller (Targets/TargetsEditor.tsx).
//
// dismissOnBackdropPress=false + onRequestClose wired to "keep editing", not
// to the discard: a stray tap-out or Android hardware back must not read as
// consent to lose the edit, same non-negotiable the ConfirmFn seam encodes
// for Alert/window.confirm ("resolves false on cancel AND on dismiss").
//
// Copy lives under the `common` i18n namespace (not `targets.*`): the concept
// is generic ("you have unsaved changes"), not specific to any one editor.

export type UnsavedChangesDialogProps = {
  theme: UiTheme;
  visible: boolean;
  /** User confirmed — the caller performs the actual discard + exit. */
  onDiscard: () => void;
  /** Cancel, tap-out, or dismiss — all read as "keep editing", never consent. */
  onKeepEditing: () => void;
};

export function UnsavedChangesDialog({
  theme,
  visible,
  onDiscard,
  onKeepEditing,
}: UnsavedChangesDialogProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);

  return (
    <Dialog
      theme={theme}
      visible={visible}
      onRequestClose={onKeepEditing}
      dismissOnBackdropPress={false}
      title={t("common.unsavedChanges.title", {
        defaultValue: "Discard changes?",
      })}
    >
      <Text style={styles.body}>
        {t("common.unsavedChanges.body", {
          defaultValue: "You'll lose your unsaved edits.",
        })}
      </Text>
      <View style={styles.actions}>
        <Button
          theme={theme}
          variant="ghost"
          label={t("common.unsavedChanges.keepEditing", {
            defaultValue: "Keep editing",
          })}
          onPress={onKeepEditing}
        />
        <Button
          theme={theme}
          variant="danger"
          label={t("common.unsavedChanges.discard", { defaultValue: "Discard" })}
          onPress={onDiscard}
        />
      </View>
    </Dialog>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    body: { color: p.ink2, fontSize: ty.sizes.base },
    actions: { flexDirection: "row", justifyContent: "flex-end", gap: s.s3 },
  });
}
